import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'
import { createContributorSession, submitContributorReport } from '@/lib/contributorWorkerClient'
import type { ReportStatus } from '@/lib/reportsStore'

export interface ReportRow {
  id: string
  photo_id: string | null
  reason: string
  status: ReportStatus
  created_at: string
  resolved_at: string | null
  resolved_by: string | null
  photo: {
    id: string
    drop: {
      id: string
      name: string
    } | null
  } | null
}

export function useReportsQuery() {
  return useQuery({
    queryKey: ['reports'],
    queryFn: () => supabaseRpc<ReportRow[]>('list_reports'),
  })
}

export interface SubmitReportParams {
  photoId: string
  reason: string
  contributorToken?: string
}

export function useSubmitReportMutation() {
  return useMutation({
    mutationFn: async (params: SubmitReportParams) => {
      if (!params.contributorToken) throw new Error('Contributor token is required.')
      const session = await createContributorSession(params.contributorToken)
      return submitContributorReport({
        sessionToken: session.sessionToken,
        photoId: params.photoId,
        reason: params.reason,
      })
    },
  })
}

export interface UpdateReportStatusParams {
  id: string
  status: ReportStatus
  resolvedAt?: string
}

export function useUpdateReportStatusMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdateReportStatusParams) =>
      supabaseRpc('update_report_status', {
        p_report_id: params.id,
        p_status: params.status,
        p_resolved_at: params.resolvedAt ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports'] })
    },
  })
}
